/**
 * Supabase Utility Functions
 * Common utilities for working with Supabase data and operations
 *
 * Type-safety note: These helpers take a runtime `tableName: string`, so Postgres
 * row shape is not known at compile time (unlike `client.from("known_table").insert(...)`
 * with `SupabaseClient<Database>`). We constrain payloads to JSON-like objects
 * (`Record<string, unknown>`) and normalize through a single bridge compatible with
 * PostgREST `GenericTable.Insert` / `Update`, which avoids `RejectExcessProperties`
 * errors when combining spreads with computed keys.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

/** JSON-like row payload for dynamic `.from(tableName)` insert/update calls. */
export type DynamicTablePayload = Record<string, unknown>;

/**
 * Validates a value is a single non-array object suitable for PostgREST JSON bodies.
 * Rejects null, primitives, and arrays (bulk insert must use {@link ensureSupabaseRows}).
 */
export function ensureSupabaseRow<T extends DynamicTablePayload>(
  data: T
): DynamicTablePayload {
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    throw new TypeError(
      `Expected a non-null plain object for Supabase insert/update, received ${data === null ? "null" : Array.isArray(data) ? "array" : typeof data}`
    );
  }
  return data;
}

/**
 * Validates an array of row objects for bulk insert.
 */
export function ensureSupabaseRows<T extends DynamicTablePayload>(
  rows: readonly T[]
): DynamicTablePayload[] {
  if (!Array.isArray(rows)) {
    throw new TypeError(
      `Expected an array of row objects for Supabase bulk insert, received ${typeof rows}`
    );
  }
  return rows.map((row, index) => {
    try {
      return ensureSupabaseRow(row);
    } catch (e) {
      throw new TypeError(`Invalid row at index ${index}: ${(e as Error).message}`);
    }
  });
}

function rowWithField(
  row: DynamicTablePayload,
  field: string,
  value: string
): DynamicTablePayload {
  return ensureSupabaseRow({
    ...row,
    [field]: value,
  });
}

/**
 * Insert data with automatic user_id assignment
 */
export async function insertWithUserId<T extends DynamicTablePayload>(
  supabase: SupabaseClient,
  tableName: string,
  data: T,
  userIdField = "user_id"
): Promise<{ data: unknown; error: unknown }> {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return { data: null, error: { message: "User not authenticated" } };
    }

    const payload = rowWithField(data, userIdField, user.id);

    const { data: insertedData, error } = await supabase
      .from(tableName)
      .insert(payload)
      .select()
      .single();

    return { data: insertedData, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Update data with automatic updated_at timestamp
 */
export async function updateWithTimestamp<T extends DynamicTablePayload>(
  supabase: SupabaseClient,
  tableName: string,
  id: string,
  updates: T,
  idField = "id"
): Promise<{ data: unknown; error: unknown }> {
  const updatesWithTimestamp = ensureSupabaseRow({
    ...updates,
    updated_at: new Date().toISOString(),
  });

  const { data, error } = await supabase
    .from(tableName)
    .update(updatesWithTimestamp)
    .eq(idField, id)
    .select()
    .single();

  return { data, error };
}

/**
 * Soft delete (mark as deleted instead of actual deletion)
 */
export async function softDelete(
  supabase: SupabaseClient,
  tableName: string,
  id: string,
  idField = "id"
): Promise<{ data: unknown; error: unknown }> {
  const patch = ensureSupabaseRow({
    deleted_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  const { data, error } = await supabase
    .from(tableName)
    .update(patch)
    .eq(idField, id)
    .select()
    .single();

  return { data, error };
}

/**
 * Bulk insert with user_id assignment
 */
export async function bulkInsertWithUserId<T extends DynamicTablePayload>(
  supabase: SupabaseClient,
  tableName: string,
  dataArray: T[],
  userIdField = "user_id"
): Promise<{ data: unknown; error: unknown }> {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return { data: null, error: { message: "User not authenticated" } };
    }

    const rows = dataArray.map((item) => rowWithField(item, userIdField, user.id));

    const { data: insertedData, error } = await supabase
      .from(tableName)
      .insert(rows)
      .select();

    return { data: insertedData, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Bulk update with automatic updated_at timestamp
 */
export async function bulkUpdateWithTimestamp<T extends DynamicTablePayload>(
  supabase: SupabaseClient,
  tableName: string,
  updates: Array<{ id: string; data: T }>,
  idField = "id"
): Promise<{ data: unknown[]; error: unknown }> {
  try {
    const updatePromises = updates.map(({ id, data }) =>
      updateWithTimestamp(supabase, tableName, id, data, idField)
    );

    const results = await Promise.all(updatePromises);

    const errors = results.filter((result) => result.error);
    if (errors.length > 0) {
      const firstError = errors[0];
      return { data: [], error: firstError?.error ?? "Unknown error" };
    }

    return { data: results.map((result) => result.data), error: null };
  } catch (error) {
    return { data: [], error };
  }
}

/**
 * Get paginated results with total count
 *
 * @param query PostgREST query builder (e.g. `supabase.from("table")`); typed loosely
 *        because the fluent chain is not modeled here without a concrete schema.
 */
export async function getPaginatedResults<T>(
  /** PostgREST fluent builder; intentionally untyped — use schema-typed `.from()` chains at call sites when possible. */
  query: any,
  page = 1,
  limit = 10
): Promise<{
  data: T[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
  error: unknown;
}> {
  try {
    const { count, error: countError } = await query.select("*", {
      count: "exact",
      head: true,
    });

    if (countError) {
      return {
        data: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: page,
        hasMore: false,
        error: countError,
      };
    }

    const offset = (page - 1) * limit;
    const { data, error: dataError } = await query.range(offset, offset + limit - 1);

    if (dataError) {
      return {
        data: [],
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / limit),
        currentPage: page,
        hasMore: false,
        error: dataError,
      };
    }

    const totalPages = Math.ceil((count || 0) / limit);
    const hasMore = page < totalPages;

    return {
      data: data || [],
      totalCount: count || 0,
      totalPages,
      currentPage: page,
      hasMore,
      error: null,
    };
  } catch (error) {
    return {
      data: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: page,
      hasMore: false,
      error,
    };
  }
}

/**
 * Execute multiple queries in parallel and combine results
 */
export async function executeParallelQueries<T>(
  queries: Array<{ key: string; query: unknown }>
): Promise<{ [key: string]: { data: T[]; error: unknown } }> {
  const queryPromises = queries.map(async ({ key, query }) => {
    try {
      const { data, error } = (await query) as {
        data: unknown;
        error: unknown;
      };
      return { key, data: data || [], error };
    } catch (error) {
      return { key, data: [], error };
    }
  });

  const results = await Promise.all(queryPromises);

  return results.reduce(
    (acc, { key, data, error }) => {
      acc[key] = { data: data as T[], error };
      return acc;
    },
    {} as { [key: string]: { data: T[]; error: unknown } }
  );
}

/**
 * Check if a record exists
 */
export async function recordExists(
  supabase: SupabaseClient,
  tableName: string,
  filters: DynamicTablePayload
): Promise<boolean> {
  let query = supabase.from(tableName).select("id", { head: true });

  for (const [field, value] of Object.entries(filters)) {
    query = query.eq(field, value);
  }

  const { data, error } = await query.single();

  return !error && !!data;
}

/**
 * Get or create a record (upsert pattern)
 */
export async function getOrCreate<T extends DynamicTablePayload>(
  supabase: SupabaseClient,
  tableName: string,
  searchFilters: DynamicTablePayload,
  createData: T
): Promise<{ data: unknown; error: unknown; created: boolean }> {
  let query = supabase.from(tableName).select("*");

  for (const [field, value] of Object.entries(searchFilters)) {
    query = query.eq(field, value);
  }

  const { data: existingData, error: findError } = await query.single();

  if (!findError && existingData) {
    return { data: existingData, error: null, created: false };
  }

  const { data: newData, error: createError } = await supabase
    .from(tableName)
    .insert(ensureSupabaseRow(createData))
    .select()
    .single();

  return {
    data: newData,
    error: createError,
    created: !createError,
  };
}
